import { useState, useEffect, useRef } from 'react'
import { Search, Plus, Pencil, Trash2, X, ChevronDown, Zap } from 'lucide-react'
import { cannedResponsesApi, CannedResponse, CreateCannedResponseDto } from '@/services/cannedResponsesApi'

const VARS = ['{nombre_cliente}', '{nombre_tienda}', '{numero_cliente}']

function VarPreview({ content, sampleVars }: { content: string; sampleVars: Record<string, string> }) {
  const preview = content
    .replace(/\{nombre_cliente\}/g, sampleVars.nombre_cliente || 'Juan')
    .replace(/\{nombre_tienda\}/g, sampleVars.nombre_tienda || 'Mi Tienda')
    .replace(/\{numero_cliente\}/g, sampleVars.numero_cliente || '+52 55 1234 5678')
  return (
    <div className="bg-[#075f55]/5 border border-[#075f55]/10 rounded-lg p-3 text-sm text-slate-700 whitespace-pre-wrap">
      {preview}
    </div>
  )
}

interface ModalProps {
  initial?: CannedResponse | null
  onClose: () => void
  onSave: (dto: CreateCannedResponseDto, id?: string) => Promise<void>
}

function CannedResponseModal({ initial, onClose, onSave }: ModalProps) {
  const [shortcut, setShortcut] = useState(initial?.shortcut || '')
  const [title, setTitle] = useState(initial?.title || '')
  const [content, setContent] = useState(initial?.content || '')
  const [category, setCategory] = useState(initial?.category || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const insertVar = (v: string) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart
    const end = ta.selectionEnd
    const next = content.slice(0, start) + v + content.slice(end)
    setContent(next)
    setTimeout(() => {
      ta.focus()
      ta.setSelectionRange(start + v.length, start + v.length)
    }, 0)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!shortcut.trim() || !title.trim() || !content.trim()) {
      setError('Atajo, título y contenido son requeridos.')
      return
    }
    setSaving(true)
    try {
      await onSave({ shortcut: shortcut.trim(), title: title.trim(), content: content.trim(), category: category.trim() || undefined }, initial?.id)
      onClose()
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Error al guardar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="text-slate-900 font-bold text-base">
            {initial ? 'Editar respuesta' : 'Nueva respuesta predefinida'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Shortcut */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">Atajo <span className="text-slate-400 font-normal">(escribe / en el chat)</span></label>
            <div className="flex items-center gap-1">
              <span className="text-[#075f55] font-bold text-lg leading-none">/</span>
              <input
                value={shortcut}
                onChange={e => setShortcut(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                maxLength={30}
                placeholder="ej: precio, horario, envio"
                className="flex-1 h-11 px-3 bg-[#f6f8f8] border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
              />
            </div>
          </div>

          {/* Title */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">Título</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={80}
              placeholder="ej: Consulta de precios"
              className="h-11 px-3 bg-[#f6f8f8] border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
            />
          </div>

          {/* Category */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">Categoría <span className="text-slate-400 font-normal">(opcional)</span></label>
            <input
              value={category}
              onChange={e => setCategory(e.target.value)}
              maxLength={40}
              placeholder="ej: Ventas, Soporte, General"
              className="h-11 px-3 bg-[#f6f8f8] border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-slate-900">Contenido de la respuesta</label>
            <div className="flex flex-wrap gap-1 mb-1">
              {VARS.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVar(v)}
                  className="px-2 py-0.5 bg-[#075f55]/10 text-[#075f55] text-xs font-medium rounded-full hover:bg-[#075f55]/20 transition-colors"
                >
                  {v}
                </button>
              ))}
            </div>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={5}
              placeholder="Escribe la respuesta aquí. Usa las variables de arriba para personalizar."
              className="w-full p-3 bg-[#f6f8f8] border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
            />
          </div>

          {/* Preview */}
          {content && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Vista previa</p>
              <VarPreview content={content} sampleVars={{ nombre_cliente: 'Juan', nombre_tienda: 'Mi Tienda', numero_cliente: '+52 55 1234 5678' }} />
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="flex-1 h-11 rounded-xl bg-[#075f55] text-white font-semibold text-sm hover:bg-[#064e46] transition-colors disabled:opacity-60">
              {saving ? 'Guardando...' : initial ? 'Guardar cambios' : 'Crear respuesta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function CannedResponsesPage() {
  const [items, setItems] = useState<CannedResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<CannedResponse | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const data = await cannedResponsesApi.list()
      setItems(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const categories = Array.from(new Set(items.map(i => i.category).filter(Boolean) as string[])).sort()

  const filtered = items.filter(i => {
    const q = search.toLowerCase()
    const matchSearch = !q || i.shortcut.includes(q) || i.title.toLowerCase().includes(q) || i.content.toLowerCase().includes(q)
    const matchCategory = !categoryFilter || i.category === categoryFilter
    return matchSearch && matchCategory
  })

  const handleSave = async (dto: any, id?: string) => {
    if (id) {
      await cannedResponsesApi.update(id, dto)
    } else {
      await cannedResponsesApi.create(dto)
    }
    await load()
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      await cannedResponsesApi.delete(id)
      setItems(prev => prev.filter(i => i.id !== id))
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="flex-1 overflow-y-auto pb-10">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-slate-900 font-bold text-xl mb-1">Respuestas rápidas</h1>
          <p className="text-slate-500 text-sm">
            Escribe <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">/atajo</kbd> en el chat para insertar respuestas predefinidas.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setModalOpen(true) }}
          className="flex items-center gap-2 bg-[#075f55] text-white px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#064e46] transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Nueva
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por atajo o contenido..."
            className="w-full h-10 pl-9 pr-4 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
          />
        </div>
        {categories.length > 0 && (
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="h-10 pl-3 pr-8 bg-white border border-slate-200 rounded-xl text-sm appearance-none focus:ring-2 focus:ring-[#075f55] focus:border-transparent outline-none"
            >
              <option value="">Todas las categorías</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-7 h-7 border-2 border-[#075f55] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center gap-4">
          <div className="w-14 h-14 bg-[#075f55]/10 rounded-full flex items-center justify-center">
            <Zap className="w-7 h-7 text-[#075f55]" />
          </div>
          <div className="text-center">
            <p className="text-slate-900 font-semibold mb-1">{search ? 'Sin resultados' : 'Sin respuestas aún'}</p>
            <p className="text-slate-500 text-sm">{search ? 'Prueba con otro término de búsqueda.' : 'Crea tu primera respuesta predefinida para agilizar la atención.'}</p>
          </div>
          {!search && (
            <button
              onClick={() => { setEditing(null); setModalOpen(true) }}
              className="flex items-center gap-2 bg-[#075f55] text-white px-5 py-2.5 rounded-xl font-semibold text-sm hover:bg-[#064e46] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crear primera respuesta
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {filtered.map((item, i) => (
            <div
              key={item.id}
              className={`flex items-start gap-4 p-4 ${i < filtered.length - 1 ? 'border-b border-slate-50' : ''} hover:bg-slate-50/50 group transition-colors`}
            >
              {/* Shortcut badge */}
              <div className="flex-shrink-0 mt-0.5">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#075f55]/10 text-[#075f55] rounded-lg text-xs font-bold font-mono">
                  /{item.shortcut}
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="text-slate-900 font-semibold text-sm truncate">{item.title}</p>
                  {item.category && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs">{item.category}</span>
                  )}
                </div>
                <p className="text-slate-500 text-xs line-clamp-2">{item.content}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => { setEditing(item); setModalOpen(true) }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#075f55] hover:bg-[#075f55]/10 transition-colors"
                  title="Editar"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  disabled={deletingId === item.id}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                  title="Eliminar"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Counter */}
      {items.length > 0 && (
        <p className="text-xs text-slate-400 mt-3 text-right">{items.length}/100 respuestas</p>
      )}

      {modalOpen && (
        <CannedResponseModal
          initial={editing}
          onClose={() => { setModalOpen(false); setEditing(null) }}
          onSave={handleSave}
        />
      )}
    </div>
  )
}
